import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Tooltip,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  ClickAwayListener
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import PageHeader from '@/components/ui/PageHeader';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
  Replay as RetryIcon,
  Webhook as WebhookIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  CopyAll as CopyAll,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  WEBHOOK_STATUSES,
  getStatusColor,
  type WebhookStatus,
} from '../../constants/webhook-status';
import type { Webhook, WebhookFilters } from '../../types/webhook';
import { format as formatMomentDate } from '@/utils/time';
import webhookService from '@/services/webhookService';
import { toast } from 'react-hot-toast';
import { FilterPanel } from '../../components/WebhookFilters/FilterPanel';


export const WebhooksPage: React.FC = () => {
  const navigate = useNavigate();
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [merchantTransactionSearchTerm, setMerchantTransactionSearchTerm] = useState('');
  const [statusFiltered, setStatusFilter] = useState("webhoodsId"); // default
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sorting state
  const [sortField, setSortField] = useState<keyof Webhook>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Filter panel state
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<WebhookFilters>({
    searchTerm: '',
    statusFilter: '',
    dateRange: { startDate: '', endDate: '' },
  });


  // Dynamic breadcrumbs for webhooks
  const breadcrumbs = [
    {
      label: 'Dashboard',
      href: '/merchant/dashboard',
      onClick: () => navigate('/merchant/dashboard'),
    },
    { label: 'Webhooks', active: true },
  ];

  // Utility: format webhook dates
  const formatWebhookDate = (dateString: string): string => {
    return formatMomentDate(dateString, 'MMM DD, YYYY HH:mm');
  };

  // Utility: fetch webhooks for current page
  const fetchWebhooks = async () => {
    setIsLoading(true);
    try {
      const currentPage = page + 1; // Convert 0-based to 1-based page

      const apiResponse = await webhookService.getWebhookTransactions(currentPage, rowsPerPage);
      setWebhooks(apiResponse.results || []);
      setTotalCount(apiResponse.count || 0);
    } catch (error) {
      setWebhooks([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, [page, rowsPerPage, refreshTrigger]);



  // Generate dynamic subtitle
  // const getSubtitle = () => {
  //   const parts: string[] = [];

  //   if (statusFilter) {
  //     parts.push(
  //       `Status: ${WEBHOOK_STATUSES[statusFilter as WebhookStatus] || statusFilter}`
  //     );
  //   }

  //   if (transactionSearchTerm) {
  //     parts.push(`Transaction ID: "${transactionSearchTerm}"`);
  //   }
  //   if (merchantTransactionSearchTerm) {
  //     parts.push(`Merchant Transaction ID: "${merchantTransactionSearchTerm}"`);
  //   }

  //   const totalWebhooks = isSearchMode ? webhooks.length : totalCount;
  //   const baseText = isSearchMode ? 'Search results for webhook notifications' : 'Manage webhook notifications';

  //   if (parts.length > 0) {
  //     return `${baseText} • ${parts.join(' • ')} (${totalWebhooks} ${totalWebhooks === 1 ? 'webhook' : 'webhooks'})`;
  //   }

  //   return `${baseText} (${totalWebhooks} ${totalWebhooks === 1 ? 'webhook' : 'webhooks'})`;
  // };

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    // useEffect will automatically fetch data when page changes
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // useEffect will automatically fetch data when rowsPerPage changes
  };

  // Sorting handlers
  // const handleSort = (field: keyof Webhook) => {
  //   if (sortField === field) {
  //     setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortField(field);
  //     setSortDirection('asc');
  //   }
  //   setPage(0);
  //   // Note: For server-side sorting, you would need to pass sort parameters to the API
  //   // For now, we'll just update the state for UI purposes
  // };

  // Get sort icon for column
  const getSortIcon = (field: keyof Webhook) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  // Retry webhook handler
  const handleRetryWebhook = async (transactionId?: string) => {
    setIsLoading(true);
    try {
      const res = await webhookService.getRetryWebhookById(transactionId || '');
      if (res?.success === true) {
        toast.success(res.message || 'Webhook retried successfully.', {
          style: { background: '#43a047', color: '#fff' },
        });
        // Refresh webhooks after successful retry by triggering useEffect
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error(res.message || 'Webhook retry failed.', {
          style: { background: '#d32f2f', color: '#fff' },
        });
      }
    } catch (error) {
      toast.error('Error occurred while retrying webhook.', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Drawer handlers
  const handleOpenFilterDrawer = (webHookDetails?: Webhook) => {
    setSelectedWebhook(webHookDetails || null);
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
  };

  // Table columns configuration
  const columns = [
    { field: 'transaction_id', headerName: 'Transaction ID', sortable: true, align: 'left' as const },
    { field: 'merchant_transaction_id', headerName: 'Merchant Transaction ID', sortable: true, align: 'left' as const },
    { field: 'webhook_status', headerName: 'Webhook Status', sortable: true, align: 'center' as const },
    { field: 'webhook_status_code', headerName: 'Webhook Status Code', sortable: true, align: 'center' as const },
    { field: 'retry_count', headerName: 'Retry Count', sortable: false, align: 'center' as const },
    { field: 'created_at', headerName: 'Created at', sortable: false, align: 'left' as const },
    { field: 'updated_at', headerName: 'Updated at', sortable: true, align: 'left' as const },
  ];

  // const onStatusFilterChange = async (status: string) => {
  //   const statusData = status.trim();
  //   setStatusFilter(statusData); // <-- update the filter state!
  //   if (statusData) {
  //     try {
  //       setIsLoading(true);
  //       const response = await webhookService.getWebhookStatusData(statusData);
  //       setWebhooks(response.results || []);
  //       setTotalCount(response.count || 0);

  //       //setSearchTerm(''); // Don't set searchTerm to status
  //     } catch (error) {
  //       setWebhooks([]);
  //       setTotalCount(0);
  //       console.error('Fetch error:', error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   } else {
  //     // If filter is cleared, reload all transactions
  //     setPage(0);
  //     fetchWebhooks();
  //   }
  // };

  const onClearFilters = () => {

    setTransactionSearchTerm('');
    setMerchantTransactionSearchTerm('');
    setStatusFilter('');
    setPage(0);
    setIsSearchMode(false);
    setStatusFilter('webhoodsId'); // reset to default
    // useEffect will automatically fetch data when page changes
  };

  // Search handler: fetch webhooks by search terms
  const onSearchTermChange = async () => {
    const transactionId = transactionSearchTerm.trim();
    const merchantId = merchantTransactionSearchTerm.trim();

    if (transactionId || merchantId) {
      setIsLoading(true);
      try {
        let response;

        if (transactionId && merchantId) {
          // Case 1: Both transaction_id and merchant_transaction_id
          response = await webhookService.getWebhookById(transactionId, merchantId);
        } else if (transactionId) {
          // Case 3: Only transaction_id
          response = await webhookService.getWebhookById(transactionId);
        } else {
          // Case 2: Only merchant_transaction_id
          response = await webhookService.getWebhookById('', merchantId);
        }

        // Update state - this will trigger re-render
        setWebhooks(response.results || []);
        setTotalCount(response.count || 0);
        setIsSearchMode(true);
        setPage(0); // Reset to first page when searching
      } catch (error) {
        setWebhooks([]);
        setTotalCount(0);
        console.error('Fetch error:', error);
        toast.error('Error occurred while searching webhooks.', {
          style: { background: '#d32f2f', color: '#fff' },
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      setWebhooks([]);
      setTotalCount(0);
      setIsSearchMode(false);
    }
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  const handleFiltersChange = (newFilters: WebhookFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      setPage(0);

      // Apply date range filter if selected
      if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
        const formatDate = (dateStr: string) => {
          const date = new Date(dateStr);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        };

        const startDate = formatDate(filters.dateRange.startDate);
        const endDate = formatDate(filters.dateRange.endDate);

        const response = await webhookService.getWebhookDateRangeData(startDate, endDate);
        setWebhooks(response.results || []);
        setTotalCount(response.count || 0);
        setIsFilterPanelOpen(false);
        return;
      }

      // Apply status filter if selected
      if (filters.statusFilter) {
        const statuses = filters.statusFilter.split(',').filter(Boolean);
        if (statuses.length > 0) {
          const response = await webhookService.getWebhookStatusData(statuses[0]);
          setWebhooks(response.results || []);
          setTotalCount(response.count || 0);
          setIsFilterPanelOpen(false);
          return;
        }
      }

      // If no filters, reload all transactions
      await fetchWebhooks();
    } catch (error) {
      console.error('Error applying filters:', error);
      setWebhooks([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsFilterPanelOpen(false);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      searchTerm: '',
      statusFilter: '',
      dateRange: { startDate: '', endDate: '' },
    });
    onClearFilters();
  };

  // Individual search term handlers
  const handleTransactionSearchChange = (value: string) => {
    setTransactionSearchTerm(value);
  };

  const handleMerchantTransactionSearchChange = (value: string) => {
    setMerchantTransactionSearchTerm(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchTermChange();
    }
  };


  // Refresh handler
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Page header actions
  const headerActions: PageHeaderAction[] = [
    {
      label: 'Refresh',
      icon: <RefreshIcon sx={{ fontSize: 16, color: '#17A6DA' }} />,
      onClick: handleRefresh,
      variant: 'outlined',
      size: 'medium',
      loading: isLoading,
    },
  ];

  // For server-side pagination, we use webhooks directly (no client-side filtering/sorting)
  // But in search mode, we need client-side pagination
  const displayWebhooks = isSearchMode
    ? webhooks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : webhooks;

  return (
  
      <Box>
        {/* Page Header */}
        <PageHeader
          title="Webhooks"
          //subtitle={getSubtitle()}
          //breadcrumbs={breadcrumbs}
          actions={headerActions}
          showDivider={false}
        />

        {/* Webhook Summary Cards */}
        {/* <WebhookSummaryCards webhooks={displayWebhooks} /> */}

        {/* Webhook Filters */}

        <Box
          mb={3}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            width: '100%',
            overflowX: 'hidden',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
        >
          {/* Main Filter Row */}
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
          >
            {/* Search Type Selector */}
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 180 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            >
              <Select
                value={statusFiltered}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="webhoodsId">Transaction ID</MenuItem>
                <MenuItem value="merchantId">Merchant Transaction ID</MenuItem>
              </Select>
            </FormControl>

            {/* Search Input */}
            {statusFiltered === "webhoodsId" && (
              <TextField
                variant="outlined"
                type="search"
                placeholder="Search by Transaction ID"
                value={transactionSearchTerm}
                onChange={(e) => handleTransactionSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: { xs: 'unset', sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: 300 },
                  '& .MuiOutlinedInput-root': {
                    background: 'white',
                  },
                }}
              />
            )}

            {statusFiltered === "merchantId" && (
              <TextField
                variant="outlined"
                type="search"
                placeholder="Search by Merchant ID"
                value={merchantTransactionSearchTerm}
                onChange={(e) => handleMerchantTransactionSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: { xs: 'unset', sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: 300 },
                  '& .MuiOutlinedInput-root': {
                    background: 'white',
                  },
                }}
              />
            )}
          </Box>

          {/* Action Buttons Row */}
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            {/* Add Filters Button */}
            <Button
              ref={filterButtonRef}
              variant="outlined"
              size="small"
              onClick={handleFilterPanelOpen}
              startIcon={<AddIcon sx={{ color: '#17A6DA' }} />}
              endIcon={<FilterIcon sx={{ color: '#17A6DA' }} />}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                textTransform: 'none',
              }}
            >
              Add filters
            </Button>

            {/* Clear Filters Button */}
            <Button
              size="small"
              variant="text"
              onClick={onClearFilters}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                textTransform: 'none',
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>

        {/* Filter Panel */}
        {isFilterPanelOpen && (
          <ClickAwayListener onClickAway={handleFilterPanelClose}>
            <Box>
              <FilterPanel
                open={isFilterPanelOpen}
                anchorEl={filterButtonRef.current}
                onClose={handleFilterPanelClose}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearAllFilters}
              />
            </Box>
          </ClickAwayListener>
        )}

        {/* HTML Table with Pagination */}
        <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
          <TableContainer sx={{
            maxHeight: { xs: 400, sm: 600 },
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
          }}>
            <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
              <TableHead>
              <TableRow>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Transaction ID</TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Merchant Transaction ID</TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Webhook Status</TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Webhook Status Code</TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Retry Count</TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Created at</TableCell>

                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Updated at</TableCell>
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body1" color="textSecondary">
                          Loading webhooks...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : displayWebhooks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No webhooks found matching your search criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayWebhooks.map((webhook) => (
                    <TableRow key={webhook?.transaction_id || webhook?.merchant_transaction_id} hover>
                      <TableCell>

                        <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .copy-icon': { opacity: 1 } }}>
                          <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {String(webhook.transaction_id)}
                          </Typography>
                          <Tooltip title="Copy to clipboard">
                            <IconButton
                              size="small"
                              onClick={() => navigator.clipboard.writeText(String(webhook.transaction_id))}
                              className="copy-icon"
                              sx={{ ml: 0.5, p: 0.25, opacity: 0, transition: 'opacity 0.2s' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: '15px' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>

                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .copy-icon': { opacity: 1 } }}>
                          <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', color: '#17A6DA' } }}>
                            {String(webhook.merchant_transaction_id)}
                          </Typography>
                          <Tooltip title="Copy to clipboard">
                            <IconButton
                              size="small"
                              onClick={() => navigator.clipboard.writeText(String(webhook.merchant_transaction_id))}
                              className="copy-icon"
                              sx={{ ml: 0.5, p: 0.25, opacity: 0, transition: 'opacity 0.2s' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: '15px' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={`Original transaction status: ${String(webhook.webhook_status)}`}>
                          <Chip
                            label={String(webhook.webhook_status)}
                            size="small"
                            color={String(webhook.webhook_status) === 'SUCCESS' ? 'success' : 'error'}
                            variant="outlined"
                            sx={{
                              fontWeight: 'normal',
                              fontSize: '0.75rem',
                              borderRadius: '12px',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={String(webhook.webhook_status_code)}>
                          {webhook.webhook_status_code ? (
                            <Chip
                              label={WEBHOOK_STATUSES[webhook.webhook_status_code as WebhookStatus] || webhook.webhook_status_code}
                              size="small"
                              color={
                                getStatusColor(webhook.webhook_status_code as WebhookStatus) as
                                | 'success'
                                | 'error'
                                | 'warning'
                                | 'info'
                                | 'primary'
                              }
                              variant="outlined"
                              sx={{
                                fontWeight: 'normal',
                                fontSize: '0.75rem',
                                borderRadius: '12px',
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          ) : (
                            <Chip
                              label="N/A"
                              size="small"
                              color="default"
                              variant="outlined"
                              sx={{
                                fontWeight: 'normal',
                                fontSize: '0.75rem',
                                borderRadius: '12px',
                                '& .MuiChip-label': { px: 1 },
                              }}
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>


                          {Number(webhook.tries)}

                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatWebhookDate(String(webhook.created_at))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatWebhookDate(String(webhook.updated_at))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenFilterDrawer(webhook)}
                            title="View Details"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          {
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRetryWebhook(webhook.transaction_id)}
                              title="Retry Webhook"
                            >
                              <RetryIcon fontSize="small" />
                            </IconButton>
                          }
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={isSearchMode ? webhooks.length : totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
          />
        </Paper>

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={isFilterDrawerOpen}
          onClose={handleCloseFilterDrawer}
          sx={{
            '& .MuiDrawer-paper': {
              width: {
                xs: '320px',   // Fixed px width for mobile devices
                sm: '420px',   // Tablet
                md: '700px',   // Desktop
                lg: '700px',    // Full width on large screens
              },
              padding: 2,
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'right' }}>
              <label onClick={handleCloseFilterDrawer} style={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: "#17A6DA", fontSize: "30px" }} />
              </label>
            </Box>
            {/* Show JSON data if selectedWebhook exists */}
            {selectedWebhook ? (
              <Box
                sx={{
                  background: '#f5f5f5',
                  borderRadius: 2,
                  p: 2,
                  mt: 0,
                  fontFamily: 'monospace',
                  fontSize: 14,
                  overflow: 'auto',
                  maxHeight: 550,
                  maxWidth: 850,
                  position: 'relative',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                    Webhook Data (JSON)
                  </Typography>
                  <IconButton
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(selectedWebhook, null, 2));
                      toast.success('JSON copied to clipboard!', {
                        style: { background: '#43a047', color: '#fff' },
                      });
                    }}
                  >
                    <CopyAll fontSize="small" />
                  </IconButton>
                </Box>
                <pre style={{ margin: 0 }}>
                  <pre>
                    {JSON.stringify(
                      {
                        request_payload: selectedWebhook.request_payload,
                        response_payload: selectedWebhook.response_payload,
                      },
                      null,
                      2
                    )}
                  </pre>

                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Select a webhook to view its JSON data.
              </Typography>
            )}
          </Box>
        </Drawer>

      </Box>
    
  );
};

export default WebhooksPage;
